import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Mail, Search } from 'lucide-react';
import { Combobox } from '@headlessui/react';
import { Button } from '../../ui/button';
import {
  resetAdminOnboardingTour,
  searchAdminUsers,
  type AdminUserSearchResult,
} from '../../../lib/adminTools';

type OnboardingTourSectionProps = {
  theme: 'light' | 'dark';
  identity?: { uid?: string | null; email?: string | null } | null;
};

const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_LENGTH = 2;
const SUCCESS_DISPLAY_MS = 5000;
const SUCCESS_FADE_MS = 300;

export function OnboardingTourSection({ theme, identity }: OnboardingTourSectionProps) {
  const isDark = theme === 'dark';

  const adminIdentity = useMemo(() => {
    if (!identity?.uid) return null;
    return { uid: String(identity.uid), email: identity.email || null };
  }, [identity]);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AdminUserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUserSearchResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [successVisible, setSuccessVisible] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);

  const runSearch = useCallback(
    (searchTerm: string) => {
      const requestId = ++searchRequestIdRef.current;
      setSearching(true);
      setRequestError(null);

      searchAdminUsers(adminIdentity, searchTerm)
        .then((payload) => {
          if (requestId !== searchRequestIdRef.current) return;
          setSearching(false);
          const users = payload?.users || [];
          setResults(users);
          setValidationMessage(
            searchTerm.trim() && users.length === 0 ? 'No user found with that email.' : null,
          );
        })
        .catch((error: unknown) => {
          if (requestId !== searchRequestIdRef.current) return;
          setSearching(false);
          setResults([]);
          setValidationMessage(null);
          setRequestError(String((error as Error)?.message || 'Unable to search for users.'));
        });
    },
    [adminIdentity],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      searchRequestIdRef.current += 1;
    };
  }, []);

  // Success messages auto-hide after ~5s (fade in, then fade out and unmount so
  // hidden state never reserves layout space). Errors persist until the next
  // action or dismissal — they are NOT auto-hidden.
  useEffect(() => {
    if (!successMessage) {
      setSuccessVisible(false);
      return undefined;
    }

    setSuccessVisible(false);
    let rafId = 0;
    if (typeof requestAnimationFrame === 'function') {
      rafId = requestAnimationFrame(() => setSuccessVisible(true));
    } else {
      setSuccessVisible(true);
    }

    const fadeOutTimer = setTimeout(() => setSuccessVisible(false), SUCCESS_DISPLAY_MS);
    const clearTimer = setTimeout(() => setSuccessMessage(null), SUCCESS_DISPLAY_MS + SUCCESS_FADE_MS);

    return () => {
      if (rafId && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
      clearTimeout(fadeOutTimer);
      clearTimeout(clearTimer);
    };
  }, [successMessage]);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setQuery(next);
    setSuccessMessage(null);
    setRequestError(null);

    if (selectedUser && next.trim().toLowerCase() !== selectedUser.email) {
      setSelectedUser(null);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = next.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      searchRequestIdRef.current += 1;
      setSearching(false);
      setResults([]);
      setValidationMessage(null);
      return;
    }

    debounceRef.current = setTimeout(() => runSearch(trimmed), SEARCH_DEBOUNCE_MS);
  };

  const handleSelect = (user: AdminUserSearchResult | null) => {
    setSelectedUser(user);
    if (user) {
      setQuery(user.email);
      setResults([]);
      setValidationMessage(null);
    }
    setSuccessMessage(null);
    setRequestError(null);
  };

  const handleReset = async () => {
    if (!selectedUser) return;
    setBusy(true);
    setSuccessMessage(null);
    setRequestError(null);

    try {
      const result = await resetAdminOnboardingTour(adminIdentity, selectedUser.email);
      setSuccessMessage(`✅ Onboarding tour progress reset for ${result.email}.`);
    } catch (error: unknown) {
      setRequestError(
        String((error as Error)?.message || 'Failed to reset onboarding tour progress.'),
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Developer/support shortcut: reset the CURRENT admin's own onboarding tour
   * locally (completion markers + force-launch flag for the next Dashboard
   * visit). Purely client-side — never touches the backend or other users.
   */
  const handleResetCurrentAdmin = () => {
    const uid = adminIdentity?.uid;
    const email = String(adminIdentity?.email || '').trim();
    setSuccessMessage(null);
    setRequestError(null);
    if (!uid || !email) {
      setRequestError('No admin session available.');
      return;
    }

    try {
      localStorage.removeItem(`onboarding.tour.completed.trade-proof.${uid}`);
      localStorage.removeItem(`onboarding.tour.completed.warrior-trading.${uid}`);
      sessionStorage.setItem('onboarding.tour.forceLaunch', 'true');
      setSuccessMessage('✅ Current admin onboarding tour reset.');
    } catch {
      setRequestError('Unable to reset the current admin onboarding tour.');
    }
  };

  const canAct = Boolean(selectedUser && !busy);
  const showDropdownEmptyState = query.trim().length >= MIN_SEARCH_LENGTH || searching;

  const sectionClassName = isDark
    ? 'rounded-xl border border-slate-800/70 bg-[#11151c] p-5 shadow-[0_10px_24px_rgba(2,6,23,0.35)]'
    : 'rounded-xl border border-[#d6d6d6] bg-white p-5 shadow-[0_8px_20px_rgba(15,23,42,0.05)]';
  const subtleTextClassName = isDark ? 'text-slate-400' : 'text-zinc-500';
  const fieldClassName = isDark
    ? 'rounded-lg border-slate-700 !bg-slate-950 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
    : 'border-zinc-200 bg-white text-zinc-900 placeholder:text-zinc-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-400';
  const secondaryBtnClass = 'h-10 rounded-xl border border-slate-700 px-2 sm:px-5';
  const currentAdminBtnClass = `${secondaryBtnClass} rounded-lg border shadow-sm ${isDark ? 'border-slate-700 bg-slate-800 text-white hover:border-slate-600 hover:bg-slate-700' : 'border-zinc-300 bg-white text-zinc-800 hover:border-zinc-400 hover:bg-zinc-50'}`;

  return (
    <section className={`${sectionClassName} flex flex-col`}>
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">ONBOARDING TOUR</h2>
        </div>
        <p className={`mt-0.5 text-sm leading-5 ${subtleTextClassName}`}>
          Reset the onboarding walkthrough for a specific user.
        </p>
      </div>

      {(successMessage || requestError) ? (
        <div className="mt-4 space-y-2">
          {successMessage ? (
            <div
              data-testid="admin-tour-status-message"
              className={`rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-100 transition-opacity duration-300 dark:text-emerald-100 ${successVisible ? 'opacity-100' : 'opacity-0'}`}
            >
              {successMessage}
            </div>
          ) : null}
          {requestError ? (
            <div
              data-testid="admin-tour-error-message"
              className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-100 dark:text-rose-100"
            >
              {requestError}
            </div>
          ) : null}
        </div>
      ) : null}

      <label className="mt-auto block text-sm font-medium">
        User Email
        <div className="mt-2 flex flex-nowrap items-center gap-1 sm:gap-2">
          <div className="min-w-[120px] max-w-[385px] flex-[1_1_385px]">
            <Combobox value={selectedUser} onChange={handleSelect} nullable>
              <div className="relative">
                <Combobox.Input
                  data-testid="admin-tour-email-search"
                  aria-label="User Email"
                  className={`h-10 w-full border bg-transparent pl-9 pr-9 text-sm shadow-sm focus:outline-none ${fieldClassName}`}
                  displayValue={(user: AdminUserSearchResult | null) => (user ? user.email : query)}
                  onChange={handleQueryChange}
                  placeholder="Search by email..."
                  autoComplete="off"
                />
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-zinc-500" />
                {searching ? (
                  <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-blue-500" />
                ) : null}

              <Combobox.Options
                className={`absolute z-30 mt-2 max-h-72 w-full overflow-auto rounded-2xl border py-2 shadow-xl ring-1 ring-black/5 focus:outline-none ${
                  isDark
                    ? 'border-slate-800 bg-slate-900 text-slate-100 ring-white/10'
                    : 'border-zinc-200 bg-white text-zinc-900 ring-black/5'
                }`}
              >
                {results.length > 0 ? (
                  results.map((user) => (
                    <Combobox.Option
                      key={user.id}
                      value={user}
                      className={({ active }) =>
                        [
                          'mx-2 flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2 text-sm transition-colors',
                          active
                            ? 'bg-blue-50 text-blue-900 dark:bg-blue-500/15 dark:text-blue-100'
                            : isDark
                              ? 'text-slate-200'
                              : 'text-zinc-700',
                        ].join(' ')
                      }
                    >
                      {({ selected }) => (
                        <>
                          <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                          <span className="truncate">{user.email}</span>
                          {selected ? <span className="ml-auto text-blue-500">✓</span> : null}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                ) : (
                  <div className={`px-4 py-1.5 text-sm ${subtleTextClassName}`}>
                    {searching
                      ? 'Searching…'
                      : showDropdownEmptyState
                        ? 'No user found with that email.'
                        : 'Type at least 2 characters to search by email.'}
                  </div>
                )}
              </Combobox.Options>
              </div>
            </Combobox>
            </div>

            <div className="flex shrink-0 flex-nowrap items-center gap-1 sm:gap-2">
              <Button
                type="button"
                variant="secondary"
                className={`${secondaryBtnClass} shrink-0 whitespace-nowrap`}
                onClick={() => {
                  void handleReset();
                }}
                disabled={!canAct}
                data-testid="admin-reset-tour-btn"
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Reset Tour
              </Button>
              <Button
                type="button"
                variant="secondary"
                className={`${currentAdminBtnClass} shrink-0 whitespace-nowrap`}
                onClick={handleResetCurrentAdmin}
                data-testid="admin-reset-current-admin-btn"
              >
                Reset Current Admin
              </Button>
            </div>
          </div>
        </label>

        {validationMessage ? (
          <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-100 dark:text-amber-100" data-testid="admin-tour-validation-message">
            {validationMessage}
          </div>
        ) : null}
    </section>
  );
}

export default OnboardingTourSection;
